import { format, formatDistance, isValid, parseISO } from "date-fns";
import { enUS, et, lt, pl, ru } from "date-fns/locale";
import { useI18nStore } from "@store/i18n/i18nStore";
import { isNullOrUndefined } from "@utils/common";
import { DEFAULT_CURRENCY_SETTINGS } from "@/constants";

export type TFormatDateLocale = "lt" | "en" | "ru" | "ee" | "pl";

const getDateFnsLocale = (locale: TFormatDateLocale) => {
  switch (locale) {
    case "en":
      return enUS;
    case "ru":
      return ru;
    case "ee":
      return et;
    case "pl":
      return pl;
    default:
      return lt;
  }
};

export const formatDate = (
  date: string | Date,
  formatPattern = "yyyy-MM-dd",
  locale: TFormatDateLocale = useI18nStore.getState().locale,
) => {
  const parsedDate = typeof date === "string" ? parseISO(date) : date;

  if (!isValid(parsedDate)) {
    return "";
  }

  try {
    return format(parsedDate, formatPattern, {
      locale: getDateFnsLocale(locale),
    });
  } catch (error) {
    console.error(error);
    // If the format pattern is invalid, return the default format
    return format(parsedDate, "yyyy-MM-dd", {
      locale: getDateFnsLocale(locale),
    });
  }
};

export const formatDistanceWithLocale = (
  date: string | Date,
  baseDate: string | Date,
  options: { addSuffix: boolean } = { addSuffix: true },
  locale: TFormatDateLocale = useI18nStore.getState().locale,
) => {
  const parsedDate = typeof date === "string" ? parseISO(date) : date;
  const parsedBaseDate =
    typeof baseDate === "string" ? parseISO(baseDate) : baseDate;

  return formatDistance(parsedDate, parsedBaseDate, {
    locale: getDateFnsLocale(locale),
    ...options,
  });
};

export const formatPrice = (
  price: number,
  options?: {
    locale?: string;
    currency?: string;
    hideCurrencySymbol?: boolean;
  },
): string => {
  const {
    locale = DEFAULT_CURRENCY_SETTINGS.locale,
    currency = DEFAULT_CURRENCY_SETTINGS.currency,
    hideCurrencySymbol = false,
  } = options ?? {};

  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: hideCurrencySymbol ? "code" : "symbol",
  });

  if (hideCurrencySymbol) {
    return formatter.format(price).replace(currency, "").trim();
  }

  return formatter.format(price);
};

export const formatPriceWithStyledZeros = (
  price: number,
  options?: {
    locale?: string;
    currency?: string;
  },
): string => {
  if (price === 0) {
    return `-,- - ${getCurrencySymbol(options)}`;
  } else {
    return formatPrice(price, options);
  }
};

interface IFormatDecimalNumberInputValueOptions {
  fractionDigits?: number;
  isFixed?: boolean;
}

export const formatDecimalNumberInputValue = (
  value: number,
  options?: IFormatDecimalNumberInputValueOptions,
): string => {
  const { fractionDigits = 2, isFixed = true } = options ?? {};
  const parsedValue = !isNaN(value) ? value : 0;
  const formattedValue =
    isFixed && !isNullOrUndefined(fractionDigits)
      ? parsedValue.toFixed(fractionDigits)
      : parsedValue.toString();
  return formattedValue.replace(".", ",");
};

export const parseDecimalNumberInputValue = (value: string): number => {
  const parsedValue = parseFloat(
    value.replace(",", ".").replace(/[^\d.-]/g, ""),
  );
  return isNaN(parsedValue) ? 0 : parsedValue;
};

export const parseIntegerInputValue = (value: string): number => {
  return parseInt(value.replace(/[^\d.,-]/g, ""));
};

export const monthIntToString = (
  month: number,
  stringFormat = "MMMM",
  locale: TFormatDateLocale = useI18nStore.getState().locale,
) => {
  return format(new Date(2000, month - 1, 1), stringFormat, {
    locale: getDateFnsLocale(locale),
  });
};

export const getCurrencySymbol = (
  options: {
    locale?: string;
    currency?: string;
  } = DEFAULT_CURRENCY_SETTINGS,
): string => {
  const { locale, currency } = options;

  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  });
  // @ts-expect-error - c
  const symbol = formatter
    ?.formatToParts(0)
    ?.find((x) => x.type === "currency").value;

  return symbol;
};
